import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import {
  createCustomer as createCustomerApi,
  updateCustomer as updateCustomerApi,
  deleteCustomer as deleteCustomerApi,
  changeCustomerStatus as changeCustomerStatusApi,
  getCustomerList as getCustomerListApi,
  getCustomerCountByStatusApi,
  getAllCustomerCountsByStatusApi,
  getCustomerDetailByIdApi,
  getCreditControlByAccountApi,
} from "../api";
import { ICustomerForm } from "../types";
import { queryKey } from "../constants";
import { ICommonQueryParams } from "@/types";

/**
 * Hook to fetch customer list
 */
export const useCustomerList = ({
  queryParams,
  enabled,
}: {
  queryParams: ICommonQueryParams;
  enabled: boolean;
}) => {
  return useQuery({
    queryKey: [queryKey.getCustomerList, queryParams],
    queryFn: () => getCustomerListApi(queryParams),
    enabled: enabled,
  });
};

/**
 * Hook to create a new customer
 */
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ICustomerForm) => createCustomerApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCustomerList] });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getCustomerCountByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllCustomerCountsByStatus],
      });
      toast.success("Customer created successfully");
    },
  });
};

/**
 * Hook to update an existing customer
 */
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<ICustomerForm> }) =>
      updateCustomerApi({ id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCustomerList] });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getCustomerCountByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllCustomerCountsByStatus],
      });
      toast.success("Customer updated successfully");
    },
  });
};

/**
 * Hook to delete a customer
 */
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteCustomerApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCustomerList] });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getCustomerCountByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllCustomerCountsByStatus],
      });
      toast.success("Customer deleted successfully");
    },
  });
};

/**
 * Hook to change customer status
 */
export const useChangeCustomerStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { statuscode: number } }) =>
      changeCustomerStatusApi({ id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getCustomerList] });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getCustomerCountByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllCustomerCountsByStatus],
      });
      toast.success("Customer status updated successfully");
    },
  });
};

/**
 * Hook to get customer count by status code (optional filter)
 */
export const useCustomerCountByStatus = (statuscode?: number) => {
  return useQuery({
    queryKey: [queryKey.getCustomerCountByStatus, statuscode],
    queryFn: () => getCustomerCountByStatusApi(statuscode),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to get all customer counts grouped by status code
 */
export const useAllCustomerCountsByStatus = () => {
  return useQuery({
    queryKey: [queryKey.getAllCustomerCountsByStatus],
    queryFn: () => getAllCustomerCountsByStatusApi(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to fetch customer/account details by ID
 */
export const useCustomerDetail = (accountId: string) => {
  return useQuery({
    queryKey: [queryKey.getCustomerDetail, accountId],
    queryFn: () => getCustomerDetailByIdApi(accountId),
    enabled: Boolean(accountId),
  });
};

/**
 * Hook to fetch credit control history for an account
 */
export const useCreditControlHistory = (accountId: string) => {
  return useQuery({
    queryKey: [queryKey.getCreditHistory, accountId],
    queryFn: () => getCreditControlByAccountApi(accountId),
    enabled: Boolean(accountId),
  });
};
